<script>
import { ref, onMounted, toRefs, watch, reactive, computed } from "vue";
import CTAButton from "../components/CTAButton.vue";
import { useTransferEffect } from "../functions";
import { useScrollEffect } from "../functions";

export default {
	components: {
		CTAButton,
	},
	props: ["windowWidth", "windowHeight", "scrollTop", "videoReady"],
	setup(props) {
		const { windowWidth, windowHeight, scrollTop, videoReady } = toRefs(
			props
		);

		const transitions = ref(null);
		const video = ref(null);
		const canvasRef = ref(null);
		const textMinor = ref(null);
		const textMajor = ref(null);
		const scrollIndicator = ref(null);
		const title = ref(null);
		const videoPlaying = ref(true);
		const op = ref(0);

		const videoSrc = reactive({
			pc: `${process.env.BASE_URL}video/THE_4.mp4`,
			mobile: `${process.env.BASE_URL}video/THE_4_mobile.mp4`,
		});

		let handleTransitions = null;
		let majorFz = 0;
		let minorFz = 0;

		onMounted(() => {
			majorFz = window.getComputedStyle(textMajor.value).fontSize;
			minorFz = window.getComputedStyle(textMinor.value).fontSize;

			window.addEventListener("resize", () => {
				majorFz = window.getComputedStyle(textMajor.value).fontSize;
				minorFz = window.getComputedStyle(textMinor.value).fontSize;
			});

			video.value.addEventListener("loadedmetadata", () => {
				handleTransitions = new useTransferEffect(
					{
						parent: transitions.value,
						intensity:
							transitions.value.dataset.intensity || undefined,
						speed: transitions.value.dataset.speed || undefined,
						easing: transitions.value.dataset.easing || undefined,
						displacementImage:
							transitions.value.dataset.displacement,
					},
					canvasRef.value,
					"video",
					video.value
				);
			});
		});

		watch(
			() => videoReady.value,
			(nv, ov) => {
				if (nv) {
					setTimeout(() => {
						op.value = 1;
						handleTransitions.transition(1);
					}, 500);
				}
			}
		);

		const majorCoeff = useScrollEffect(
			0,
			1 * windowHeight.value,
			0,
			0.013 * windowWidth.value + 24.9
		);
		const minorCoeff = useScrollEffect(
			0,
			1 * windowHeight.value,
			0,
			0.017 * windowWidth.value - 2
		);
		const titleCoeff = useScrollEffect(
			windowHeight.value - 150,
			1 * windowHeight.value,
			-0.5,
			1.1
		);
		const scrInOpacityCoeff = useScrollEffect(
			0,
			0.2 * windowHeight.value,
			1,
			0
		);
		const scrInTopCoeff = useScrollEffect(0, windowHeight.value, 0, 50);

		watch(
			() => videoPlaying.value,
			(nv, ov) => {
				if (nv !== ov) {
					if (nv) {
						video.value.play();
						handleTransitions.startAnim();
					} else {
						video.value.pause();
					}
				}
			}
		);

		watch(
			() => scrollTop.value,
			(nv, ov) => {
				// text blur
				if (nv > 0 && nv <= windowHeight.value) {
					textMajor.value.style.filter = `blur(${nv * majorCoeff.a +
						majorCoeff.b}px)`;
					textMinor.value.style.filter = `blur(${nv * minorCoeff.a +
						minorCoeff.b}px)`;
				} else if (nv > 2.5 * windowHeight.value) {
					textMajor.value.style.filter = "blur(0px)";
					textMinor.value.style.filter = "blur(0px)";
				} else if (nv > windowHeight.value) {
					textMajor.value.style.filter = `blur(${windowHeight.value *
						majorCoeff.a +
						majorCoeff.b}px)`;
					textMinor.value.style.filter = `blur(${windowHeight.value *
						minorCoeff.a +
						minorCoeff.b}px)`;
				} else {
					textMajor.value.style.filter = "blur(0px)";
					textMinor.value.style.filter = "blur(0px)";
				}

				// title fade in
				if (nv > windowHeight.value - 150) {
					title.value.style.opacity = `${nv * titleCoeff.a +
						titleCoeff.b}`;
				}

				// control video playing
				if (nv > 2.5 * windowHeight.value) {
					videoPlaying.value = false;
				} else {
					videoPlaying.value = true;
				}

				// scroll indicator fade up
				scrollIndicator.value.style.opacity = `${nv *
					scrInOpacityCoeff.a +
					scrInOpacityCoeff.b}`;
				scrollIndicator.value.style.transform = `translateY(${-nv *
					scrInTopCoeff.a -
					scrInTopCoeff.b}px)`;
			}
		);
		return {
			scrollTop,
			transitions,
			windowWidth,
			videoSrc,
			video,
			canvasRef,
			textMajor,
			textMinor,
			title,
			scrollIndicator,
			op,
		};
	},
};
</script>

<template>
	<section id="story" :style="{ opacity: op }">
		<div
			class="sticky-wrapper"
			data-displacement="filter.jpg"
			data-intensity="0.4"
			data-speed="1.2"
			data-easing="power3.inOut"
			ref="transitions"
		>
			<video
				id="story_video_c1"
				:src="windowWidth > 768 ? videoSrc.pc : videoSrc.mobile"
				ref="video"
				loop
				muted
				autoplay
				playsinline
				preload="auto"
			></video>
			<canvas ref="canvasRef"></canvas>
			<div class="text-container">
				<div class="the-4-text">
					<span class="text_minor text_en_thin" ref="textMinor">
						THE
					</span>
					<div class="text_major text_en_light" ref="textMajor"
						>4</div
					>
				</div>
			</div>
			<div
				:class="['scroll-indicator-box', { shake: scrollTop < 5 }]"
				ref="scrollIndicator"
			>
				<span class="scroll-text text_en_light">SCROLL</span>
				<div class="scroll-indicator"></div>
			</div>
		</div>
	</section>
	<section id="title">
		<div class="sticky-wrapper" ref="title">
			<div class="title_main text_en_thin">
				<h1>THE 4 Gran Coupé</h1>
				<h2 class="title_sub text_ch_medium shine">
					<span class="inner_title_sub">鋒格自</span><span>成</span>
				</h2>
			</div>
			<CTAButton
				:href1="'#form'"
				:GAData1="{
					event_type: 'click',
					event_category: 'index',
					event_label: 'testdrive',
				}"
				:GAData2="{}"
				>預約賞車</CTAButton
			>
		</div>
	</section>
</template>

<style scoped>
#story {
	background-color: #111;
	transition: opacity 1s;
	z-index: 59;
}

#story .sticky-wrapper {
	width: 100%;
	overflow: hidden;
	background-color: #fff;
}

#story .sticky-wrapper #story_video_c1 {
	display: none;
	position: absolute;
	width: 100%;
	height: 100%;
	object-fit: cover;
}

#story .sticky-wrapper canvas {
	position: absolute;
	width: 100%;
	height: 100%;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
}

#story .sticky-wrapper .text-container {
	position: relative;
	width: 100%;
	height: 100%;
	top: 0;
	display: flex;
	justify-content: center;
	align-items: center;
}

.the-4-text {
	position: relative;
	width: calc(0.55 * (38.9vw + 253.3px));
	height: calc(21.5vw + 216.2px);
}

.the-4-text .text_minor {
	text-shadow: 0px 1px 4px rgba(0, 0, 0, 0.15);
	-webkit-text-stroke: 0.5px #fff;
	line-height: 1;
	font-size: calc(1.7vw + 28.3px);
	letter-spacing: 3.6px;
}

.the-4-text .text_major {
	position: absolute;
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%);
	height: 100%;
	width: 100%;
	font-size: calc(29.1vw + 291.6px);
	line-height: calc(21.5vw + 216.2px);
	mix-blend-mode: difference;
	z-index: 110;
	color: #ff6a6a;
}

.scroll-indicator-box {
	position: absolute;
	width: 100%;
	top: 92%;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	z-index: 100;
}

.scroll-indicator-box .scroll-text {
	text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
	font-size: 1.5rem;
	letter-spacing: 0.9px;
}

.scroll-indicator-box .scroll-indicator {
	margin-top: 10px;
	background-image: url("~@/assets/scroll_line.png");
	width: 10px;
	height: 110px;
	background-size: 1px auto;
	background-position: center;
	background-repeat: no-repeat;
}

#title {
	z-index: 67;
	width: calc(100% - 50px);
}
#title .sticky-wrapper {
	width: 100%;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	z-index: 10;
	opacity: 0;
}

#title .sticky-wrapper .title_main {
	text-align: center;
	margin-left: 50px;
}

#title .sticky-wrapper .title_main h1 {
	font-size: calc(4.6vw + 12.2px);
	line-height: 0.91;
	letter-spacing: 5px;
}

#title .sticky-wrapper .title_main .title_sub {
	position: relative;
	width: calc(100vw - 50px);
	margin-top: 50px;
	font-size: 3rem;
	line-height: 3.27;
}

#title .sticky-wrapper .title_main .title_sub span {
	letter-spacing: normal;
	font-size: inherit;
	color: #999;
}

#title .sticky-wrapper .title_main .title_sub span.inner_title_sub {
	letter-spacing: 31.2px;
}

#title .sticky-wrapper .CTA-button {
	margin-left: 25px;
}

@media screen and (max-width: 1200px) {
	#title .sticky-wrapper .title_main .title_sub {
		width: calc(100vw - 20px);
	}
}

@media screen and (max-width: 768px) {
	#story .sticky-wrapper .text-container .the-4-text .text_minor {
		letter-spacing: 1.45px;
		font-family: "BMWTypeNextLatinTT-Light", serif;
	}
	#story .sticky-wrapper .scroll-indicator-box {
		top: 82.5%;
	}
	#story .sticky-wrapper .scroll-indicator-box .scroll-text {
		font-size: 1rem;
		letter-spacing: 0.6px;
		font-family: "BMWTypeNextLatinTT-Thin", serif;
	}
	#title .sticky-wrapper .title_main h1 {
		line-height: 1.13;
		letter-spacing: 1.5px;
		font-family: "BMWTypeNextLatinTT-Light", serif;
	}
	#title .sticky-wrapper .title_main .title_sub {
		font-size: 1.8rem;
		line-height: 2.5;
		margin-top: 15px;
		width: 100vw;
	}
	#title .sticky-wrapper .title_main .title_sub span.inner_title_sub {
		letter-spacing: 9.6px;
	}
}

@media screen and (max-width: 650px) {
	#story .sticky-wrapper canvas {
		top: auto;
		bottom: 0;
		transform: translate(-50%, 0%);
	}
}
</style>
